// @flow

import * as React from 'react';
import Radio from '@ta/common.radio';
import { ResponsiveImage, type Source as ResponsiveImageSource } from '@ta/media.image';
import { globalize } from '@ta/common.i18n';
import { InteractionLogger } from '@ta/tracking.interactions';
import { featureIsEnabled } from '@ta/common.features';

import styles from './GeoOverviewPhotos.less';

type PhotoviewerOptions = {
  detail: number,
  geo: number,
  albumId: number,
  filter: number,
  ff?: number,
  baseMediaId?: number,
  albumViewMode: string,
  updateType: string,
};

type Photo = {|
  /** Id of the photo */
  id: number,

  /** List of responsive photo sizes */
  photoSizes: Array<ResponsiveImageSource>,
|};

type Props = {
  /** The list of photo sources */
  photos: Array<Photo>,
  /** The album id */
  id: number,
  /** The album's location id (will most likely be the geoId) */
  locationId: number,
  /** Total count of photos in the album */
  totalCount: number,
};

const ALL_FILTERS = 7;

function openPhotoviewer(locationId, geoId, albumId, mediaId) {
  const options: PhotoviewerOptions = {
    detail: locationId,
    geo: geoId,
    albumViewMode: 'grid',
    updateType: 'full',
    albumId,
    filter: ALL_FILTERS,
  };

  if (mediaId > 0) {
    options.ff = mediaId;
    options.baseMediaId = mediaId;
    options.albumViewMode = 'hero';
  }

  Radio('responsive-photoviewer').emit('load', options);
}

const shadowOverPhotos = count => (
  <div className={styles.shadowOver}>
    <div className={styles.countText}>+ {globalize.formatNumber('integer', count)}</div>
  </div>
);

const GeoOverviewPhotos = ({ photos, id, locationId, totalCount }: Props) => {
  const numItems = 4;
  if (photos.length < numItems) {
    return null;
  }

  return (
    <InteractionLogger>
      {logInteraction => (
        <div className={styles.photosWrap}>
          {photos.map((photo, index) => {
            const isLastPhoto = index === numItems - 1;
            if (index < numItems) {
              const photoIdForAlbum = isLastPhoto ? -1 : photo.id;
              return (
                <div
                  key={photo.id}
                  className={styles.photoItem}
                  onClick={e => {
                    logInteraction(e, 'linkedPhotoClick', { mediaId: photo.id });
                    openPhotoviewer(locationId, locationId, id, photoIdForAlbum);
                  }}
                >
                  <ResponsiveImage
                    sources={photo.photoSizes}
                    height="100%"
                    width="100%"
                    allowedStretch={
                      featureIsEnabled('ml_geo_photo_sort_order_from_db') ? 0 : undefined
                    }
                  />
                  {/* In case there is photo count inconsistency like ADS-6466 */}
                  {isLastPhoto ? shadowOverPhotos(totalCount || photos.length) : null}
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </InteractionLogger>
  );
};

export default GeoOverviewPhotos;
